import { useMemo, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Skeleton } from 'skullet/react'
import { toast } from 'sonner'
import { Link2, Mail } from 'lucide-react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { captureError } from '@/lib/sentry'
import { formatEnumName } from '@/lib/enum-utils'

import { useListEmailThreads, useLinkEmailThread } from '@/stores/emails.store'
import { EmailParticipantSchema, EmailThreadLiteSchema, EmailThreadStatus } from '@/gen/cortado/v1/emails_pb'
import type { EmailThreadLite } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { formatTimestampWithTime } from '@/lib/date-utils'

const SAMPLE_THREADS: EmailThreadLite[] = [
  { subject: 'Question about your fall catering packages', name: 'Jordan Ellis' },
  { subject: 'Re: Availability on October 18', name: 'Priya Raman' },
  { subject: 'Dietary notes for Saturday', name: 'Sam Okafor' },
].map(({ subject, name }, i) =>
  create(EmailThreadLiteSchema, {
    id: `sample-${i}`,
    subject,
    participants: [create(EmailParticipantSchema, { name, email: 'guest@example.com' })],
    lastMessageAt: timestampFromDate(new Date(2026, 0, 12, 9, 15)),
  }),
)

interface EntityConversationLinkerProps {
  /** Type of CRM entity this conversation will be linked to. */
  entityType: ParentTypeEnum
  /** Id of the entity this conversation will be linked to. */
  entityId: string
  /** Whether the dialog is open. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a thread is successfully linked (e.g. to refetch the panel). */
  onLinked?: () => void
  /**
   * Thread ids already linked to this entity, so they're excluded from the
   * unmatched list — linking is an add-edge operation, not a setter, so
   * without this a thread could be "linked" again for no effect.
   */
  alreadyLinkedThreadIds?: string[]
}

/**
 * EntityConversationLinker - lets a user attach an existing "needs review"
 * inbound conversation (one the automatic matcher could not place) to a CRM
 * entity — Contact, Company, Quote, or Booking — from that entity's own page,
 * via the generic `LinkEmailThread` RPC (backed by `email_thread_links`).
 * Complements the existing auto-match flow available from the inbox thread
 * detail view.
 */
export function EntityConversationLinker({
  entityType,
  entityId,
  open,
  onOpenChange,
  onLinked,
  alreadyLinkedThreadIds,
}: EntityConversationLinkerProps) {
  const [linkingThreadId, setLinkingThreadId] = useState<string | null>(null)

  const { threads, isLoading } = useListEmailThreads({
    statuses: [EmailThreadStatus.NEEDS_REVIEW],
    limit: 50,
    enabled: open,
  })
  const { link } = useLinkEmailThread()

  const linkedThreadIds = useMemo(() => new Set(alreadyLinkedThreadIds ?? []), [alreadyLinkedThreadIds])
  const unlinkedThreads = threads.filter((thread) => !linkedThreadIds.has(thread.id))
  const entityLabel = formatEnumName(entityType, ParentTypeEnum).toLowerCase()

  const handleLink = async (thread: EmailThreadLite) => {
    setLinkingThreadId(thread.id)
    try {
      await link({ threadId: thread.id, entityType, entityId })
      toast.success(`Conversation linked to ${entityLabel}`)
      onOpenChange(false)
      onLinked?.()
    } catch (error) {
      reportError(error, { feature: 'email_link_conversation_to_entity', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'entity-conversation-linker',
      })
      toast.error('Failed to link conversation', {
        description: getErrorDescription(error),
      })
    } finally {
      setLinkingThreadId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a Conversation</DialogTitle>
          <DialogDescription>
            Attach an inbound email the matcher could not place to this {entityLabel}. Only
            conversations still marked "Needs Review" are shown.
          </DialogDescription>
        </DialogHeader>

        <Skeleton
          data={isLoading ? null : unlinkedThreads}
          sample={SAMPLE_THREADS}
          label="Loading conversations"
          empty={!isLoading && unlinkedThreads.length === 0}
          emptyMessage={
            <Empty className="border border-dashed py-8">
              <EmptyMedia variant="icon">
                <Mail className="h-5 w-5" />
              </EmptyMedia>
              <EmptyDescription>No unmatched conversations need review right now.</EmptyDescription>
            </Empty>
          }
        >
          {(unlinkedThreads) => (
            <ScrollArea className="max-h-80">
              <div className="space-y-2 pr-3">
                {unlinkedThreads.map((thread) => (
                  <UnlinkedThreadRow
                    key={thread.id}
                    thread={thread}
                    isLinking={linkingThreadId === thread.id}
                    onLink={() => handleLink(thread)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </Skeleton>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface UnlinkedThreadRowProps {
  thread: EmailThreadLite
  isLinking: boolean
  onLink: () => void
}

function UnlinkedThreadRow({ thread, isLinking, onLink }: UnlinkedThreadRowProps) {
  const participant = thread.participants[0]
  const participantLabel = participant?.name || participant?.email || 'Unknown sender'

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border p-3',
        'transition-colors hover:bg-muted/40',
      )}
    >
      <div className="min-w-0 space-y-0.5">
        <p className="truncate text-sm font-medium">{thread.subject || '(No subject)'}</p>
        <p className="truncate text-xs text-muted-foreground">
          {participantLabel} &middot; {formatTimestampWithTime(thread.lastMessageAt)}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isLinking}
        onClick={onLink}
        className="shrink-0 gap-1"
      >
        <Link2 className="h-3.5 w-3.5" />
        {isLinking ? 'Linking...' : 'Link'}
      </Button>
    </div>
  )
}
